import { supabase } from './supabase';

/**
 * Likes du fil News — deux tables, deux portées (voir `0040_news_likes.sql`).
 *
 * `news_article_stats` porte le NOMBRE, lisible par tous les utilisateurs connectés.
 * `news_likes` porte le DÉTAIL, lisible par son seul auteur.
 *
 * Le client ne peut donc pas savoir qui a liké quoi, et n'a aucun moyen d'écrire le compteur : la
 * table d'agrégat n'a pas de policy d'écriture, seul le trigger y touche. Une tentative de
 * `.update()` sur `news_article_stats` ne toucherait aucune ligne et ne lèverait rien.
 */

export interface LikeState {
  /** article_id → nombre de likes. Absent de la table = zéro, jamais « inconnu ». */
  counts: Record<string, number>;
  /** Les articles que CET utilisateur a likés. */
  mine: Set<string>;
}

/**
 * DEUX REQUÊTES POUR TOUTE LA PAGE, pas une par carte.
 *
 * Dix cartes visibles font vingt requêtes si chacune interroge pour elle-même — et le double au
 * chargement de la page suivante. Les identifiants sont connus d'un bloc au moment où la page
 * arrive : autant les demander ensemble.
 *
 * Les deux appels sont indépendants et partent en parallèle. Ils ne peuvent pas être fusionnés en
 * une jointure : ce sont deux tables aux portées de lecture différentes, et c'est justement ce qui
 * garde le détail privé.
 */
export async function fetchLikeState(articleIds: string[]): Promise<LikeState> {
  const vide: LikeState = { counts: {}, mine: new Set() };
  if (articleIds.length === 0) return vide;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  // Le compteur lui-même est réservé aux utilisateurs connectés (policy `to authenticated`) : hors
  // connexion il n'y a rien à demander, et le cœur ne s'affiche pas de toute façon.
  if (!user) return vide;

  const [stats, miens] = await Promise.all([
    supabase.from('news_article_stats').select('article_id, like_count').in('article_id', articleIds),
    supabase.from('news_likes').select('article_id').eq('user_id', user.id).in('article_id', articleIds),
  ]);

  if (stats.error) throw stats.error;
  if (miens.error) throw miens.error;

  const counts: Record<string, number> = {};
  for (const ligne of (stats.data as { article_id: string; like_count: number }[]) ?? []) {
    counts[ligne.article_id] = ligne.like_count;
  }

  return {
    counts,
    mine: new Set(((miens.data as { article_id: string }[]) ?? []).map((l) => l.article_id)),
  };
}

/**
 * `ignoreDuplicates` : liker deux fois n'est pas une erreur. Sans cela un double appui, ou deux
 * appareils synchronisés, lèveraient une violation de clé primaire — et, pire, un `do update`
 * ferait repasser le trigger en INSERT et incrémenterait le compteur une seconde fois.
 */
export async function likeArticle(articleId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { error } = await supabase
    .from('news_likes')
    .upsert(
      { user_id: user.id, article_id: articleId },
      { onConflict: 'user_id,article_id', ignoreDuplicates: true },
    );
  if (error) throw error;
}

export async function unlikeArticle(articleId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { error } = await supabase
    .from('news_likes')
    .delete()
    .eq('user_id', user.id)
    .eq('article_id', articleId);
  if (error) throw error;
}
